import { ContentBriefFields, initialBrief } from "./ContentBriefFields";
import type { GenerationBrief } from "../../hooks/useArticleGeneration";
import React, { useState } from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/joy";
import { useAreasServed } from "../../hooks/useAreasServed";

interface AreaSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (areaId: number | null, brief: GenerationBrief) => Promise<unknown>;
  blogIdeaTitle: string;
}

export const AreaSelectionModal: React.FC<AreaSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  blogIdeaTitle,
}) => {
  const [selectedAreaId, setSelectedAreaId] = useState<string>("all");
  const { data: areas = [], isLoading, error } = useAreasServed();

  const [brief, setBrief] = useState(initialBrief);
  const [busy, setBusy] = useState(false);
  const handleSubmit = async () => {
    setBusy(true);
    try {
      await onSelect(
        selectedAreaId === "all" ? null : Number(selectedAreaId),
        brief,
      );
    } catch {
      /* The mutation displays the error and keeps this form open. */
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <ModalDialog
        sx={{
          width: 620,
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <DialogTitle>Select Target Service Area</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography level="body-sm" sx={{ mb: 1 }}>
              Choose which service area to focus the article on:
            </Typography>
            <Typography level="title-sm" sx={{ fontStyle: "italic" }}>
              "{blogIdeaTitle}"
            </Typography>
          </Box>

          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              Failed to load service areas. Please try again.
            </Alert>
          )}

          {!isLoading && !error && (
            <FormControl>
              <FormLabel>Service Area</FormLabel>
              <RadioGroup
                value={selectedAreaId}
                onChange={(event) => setSelectedAreaId(event.target.value)}
              >
                <Radio
                  value="all"
                  label="All Service Areas (General content)"
                  sx={{ mb: 1 }}
                />
                {areas.map((area) => (
                  <Radio
                    key={area.id}
                    value={area.id.toString()}
                    label={`${area.city}, ${area.state}`}
                    sx={{ mb: 1 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
          <Box sx={{ mt: 2 }}>
            <ContentBriefFields value={brief} onChange={setBrief} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            onClick={handleSubmit}
            loading={busy}
            disabled={isLoading || !!error}
          >
            Generate Article
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};
